// ============================================================
// audio/manager.js — Music management, toggles & source switching
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { startMusic, stopMusic } from './musicChill.js';
import { startOgMusic, stopOgMusic } from './musicOg.js';
import { stopVoidMusic } from './musicVoid.js';
import { startVoidMusic } from './musicVoid.js';
import { startPhase5Music, stopPhase5Music } from './musicPhase5.js';

/**
 * Start whichever music track is correct for the current phase + source.
 * Phase-specific tracks take precedence over the chill/OG baseline so
 * re-entering the game (e.g. Continue-Same-Char at phase 3) doesn't
 * stack two songs on top of each other.
 */
export function startCurrentMusic() {
  if (state.phase5Active)        { startPhase5Music(); }
  else if (state.phase4Active)   { startVoidMusic(); }
  else if (state.phase3Active)   { dom.phase3Audio.play().catch(() => {}); }
  else if (state.musicSource === 'mp3') { startMusic(); }
  else                           { startOgMusic(); }
}

/** Stop every music system. */
export function stopAllMusic() {
  stopMusic();
  stopOgMusic();
  stopVoidMusic();
  stopPhase5Music();
  dom.phase3Audio.pause();
}

/**
 * Smoothly fade out all music over `duration` ms, then stop everything.
 * Returns a Promise that resolves when fade is complete.
 */
export function fadeOutAllMusic(duration = 800) {
  return new Promise(resolve => {
    const steps = 20;
    const interval = duration / steps;
    let step = 0;

    // Capture starting volumes
    const bgStart = dom.bgMusic.volume;
    const p3Start = dom.phase3Audio.volume;

    // Fade Web Audio nodes via a temporary gain ramp
    if (state.audioCtx && state.musicNodes.length > 0) {
      const now = state.audioCtx.currentTime;
      state.musicNodes.forEach(n => {
        if (n.gain) {
          n.gain.setValueAtTime(n.gain.value, now);
          n.gain.linearRampToValueAtTime(0, now + duration / 1000);
        }
      });
    }

    // Fade HTML audio elements
    const fadeTimer = setInterval(() => {
      step++;
      const vol = 1 - step / steps;
      dom.bgMusic.volume = bgStart * vol;
      dom.phase3Audio.volume = p3Start * vol;
      if (step >= steps) {
        clearInterval(fadeTimer);
        stopAllMusic();
        // Restore volumes for next play
        dom.bgMusic.volume = bgStart;
        dom.phase3Audio.volume = p3Start;
        resolve();
      }
    }, interval);
  });
}

// Saved gain values per node before pausing
let _savedGains = [];

/**
 * Smoothly fade out music and pause it in place (keeps position).
 */
export function pauseMusic(duration = 600) {
  _savedGains = [];
  const steps = 20;
  const interval = duration / steps;
  let step = 0;
  const bgStart = dom.bgMusic.volume;
  const p3Start = dom.phase3Audio.volume;

  // Ramp Web Audio gains to 0
  if (state.audioCtx && state.musicNodes.length > 0) {
    const now = state.audioCtx.currentTime;
    state.musicNodes.forEach(n => {
      if (n.gain) {
        _savedGains.push({ node: n, value: n.gain.value });
        n.gain.setValueAtTime(n.gain.value, now);
        n.gain.linearRampToValueAtTime(0, now + duration / 1000);
      }
    });
  }

  // Fade HTML audio
  const fadeTimer = setInterval(() => {
    step++;
    const vol = 1 - step / steps;
    dom.bgMusic.volume = bgStart * vol;
    dom.phase3Audio.volume = p3Start * vol;
    if (step >= steps) {
      clearInterval(fadeTimer);
      dom.bgMusic.pause();
      dom.phase3Audio.pause();
      // Suspend context to save resources
      if (state.audioCtx && state.audioCtx.state === 'running') {
        state.audioCtx.suspend();
      }
      // Store volumes for restore
      dom.bgMusic._savedVol = bgStart;
      dom.phase3Audio._savedVol = p3Start;
    }
  }, interval);
}

/**
 * Smoothly fade music back in from where it was paused.
 */
export function resumeMusic(duration = 600) {
  const bgTarget = dom.bgMusic._savedVol ?? state.musicVolume;
  const p3Target = dom.phase3Audio._savedVol ?? state.musicVolume;

  // Resume context
  if (state.audioCtx && state.audioCtx.state === 'suspended') {
    state.audioCtx.resume();
  }

  // Restore Web Audio gains
  if (state.audioCtx && _savedGains.length > 0) {
    const now = state.audioCtx.currentTime;
    _savedGains.forEach(({ node, value }) => {
      if (node.gain) {
        node.gain.setValueAtTime(0, now);
        node.gain.linearRampToValueAtTime(value, now + duration / 1000);
      }
    });
    _savedGains = [];
  }

  // Resume HTML audio from 0 volume and fade up
  dom.bgMusic.volume = 0;
  dom.phase3Audio.volume = 0;
  if (state.musicPlaying) dom.bgMusic.play().catch(() => {});
  if (state.phase3Active) dom.phase3Audio.play().catch(() => {});

  const steps = 20;
  const interval = duration / steps;
  let step = 0;
  const fadeTimer = setInterval(() => {
    step++;
    const vol = step / steps;
    dom.bgMusic.volume = bgTarget * vol;
    dom.phase3Audio.volume = p3Target * vol;
    if (step >= steps) clearInterval(fadeTimer);
  }, interval);
}

/**
 * Wire up the settings-panel event listeners for music / sfx toggles
 * and music-source switching buttons.  Call once at startup.
 */
/** Apply the current music volume to all audio outputs. */
export function applyMusicVolume() {
  const vol = state.musicVolume;
  dom.bgMusic.volume = vol;
  dom.phase3Audio.volume = vol;
  // Scale Web Audio master gains
  if (state.audioCtx && state.musicNodes.length > 0) {
    state.musicNodes.forEach(n => {
      if (n.gain && n._baseGain != null) {
        n.gain.setValueAtTime(n._baseGain * vol, state.audioCtx.currentTime);
      }
    });
  }
}

export function initAudioSettings() {
  // Initialize volume from state
  state.musicVolume = state.musicVolume ?? 1;
  dom.musicVolumeSlider.value = Math.round(state.musicVolume * 100);

  // Show volume row if music is on
  if (dom.toggleMusic.classList.contains('on')) {
    dom.musicVolumeRow.classList.add('show');
  }

  // Volume slider
  dom.musicVolumeSlider.addEventListener('input', (e) => {
    e.stopPropagation();
    state.musicVolume = parseInt(e.target.value, 10) / 100;
    dom.bgMusic.volume = state.musicVolume;
    dom.phase3Audio.volume = state.musicVolume;
  });

  // Music toggle
  dom.toggleMusic.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOn = dom.toggleMusic.classList.contains('on');
    if (isOn) {
      // Turn off all music
      stopMusic();
      stopOgMusic();
      stopVoidMusic();
      stopPhase5Music();
      dom.phase3Audio.pause();
      dom.toggleMusic.classList.remove('on');
      dom.musicVolumeRow.classList.remove('show');
    } else {
      // Turn on the right music for current phase
      dom.toggleMusic.classList.add('on');
      dom.musicVolumeRow.classList.add('show');
      if (state.phase5Active) { startPhase5Music(); }
      else if (state.phase4Active) { startVoidMusic(); }
      else if (state.phase3Active) { dom.phase3Audio.play().catch(() => {}); }
      else if (state.musicSource === 'mp3') { startMusic(); }
      else { startOgMusic(); }
    }
  });

  // SFX toggle
  dom.toggleSfx.addEventListener('click', (e) => {
    e.stopPropagation();
    state.sfxEnabled = !state.sfxEnabled;
    dom.toggleSfx.classList.toggle('on', state.sfxEnabled);
  });

  // Music source buttons
  dom.srcMp3.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.musicSource === 'mp3') return;
    state.musicSource = 'mp3';
    dom.srcMp3.classList.add('active');
    dom.srcOg.classList.remove('active');
    if (state.musicPlaying) {
      stopOgMusic();
      state.musicNodes.forEach(n => { try { n.disconnect(); } catch(e){} });
      state.musicNodes = [];
      state.musicPlaying = false;
      startMusic();
    }
  });

  dom.srcOg.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.musicSource === 'og') return;
    state.musicSource = 'og';
    dom.srcOg.classList.add('active');
    dom.srcMp3.classList.remove('active');
    if (state.musicPlaying) {
      stopMusic();
      startOgMusic();
    }
  });
}
